import * as React from 'react'

import { Box, Text, useToggle } from '@factorialco/deprecated-design-system'
import sortBy from 'lodash/sortBy'

import { observer } from 'mobx-react'

import { ExplotableItem, ExplotableList } from 'modules/core/components/Explotable'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import SortableBoxList from 'modules/core/components/SortableBoxList'
import i18n from 'modules/core/lib/i18n'

import Item from 'modules/surveys/components/Form/QuestionConfigs/List/Item'
import type SurveyForm from 'modules/surveys/store/models/Form'
import type QuestionCategory from 'modules/surveys/store/models/QuestionCategory'
import type QuestionConfig from 'modules/surveys/store/models/QuestionConfig'
import type QuestionGroupConfig from 'modules/surveys/store/models/QuestionGroupConfig'
import type Survey from 'modules/surveys/store/models/v2/Survey'

import Header from './_Header'

type ItemProps = React.ComponentProps<typeof Item>

interface Props {
  form: SurveyForm | Survey
  questionGroupConfig: QuestionGroupConfig
  questionCategory: QuestionCategory
  updateQuestionConfig: ItemProps['updateQuestionConfig']
  createQuestionConfigFromDuplicate: ItemProps['createQuestionConfigFromDuplicate']
  toggleArchivedQuestionConfig: ItemProps['toggleArchivedQuestionConfig']
  reorderQuestionConfig: (config: { id: string; newPosition: number }) => void
  deleteQuestionConfig: ItemProps['deleteQuestionConfig']
}

function QuestionCategoryComponent({
  form,
  questionGroupConfig,
  questionCategory,
  updateQuestionConfig,
  createQuestionConfigFromDuplicate,
  toggleArchivedQuestionConfig,
  reorderQuestionConfig,
  deleteQuestionConfig,
}: Props) {
  const [open, toggle] = useToggle(false)

  const questionsHeader = (open: boolean) => (
    <Header
      form={form}
      questionGroupConfig={questionGroupConfig}
      questionCategory={questionCategory}
      canEdit
      expanded={open}
    />
  )

  const archivedQuestionsHeader = () => {
    const borderStyle = {
      style: 'solid',
      width: 's1',
      color: 'grey400',
    } as const

    return (
      <Box
        flexDirection="row"
        alignItems="center"
        height="s40"
        paddingLeft="s16"
        gap="s16"
        border={{
          bottom: borderStyle,
          top: borderStyle,
        }}
        flexGrow
      >
        <Text size="200" weight="medium" color="grey1000">
          {i18n.t('surveys.form.question_configs.archived_list')}
        </Text>
        <InformationTooltip
          information={i18n.t('surveys.form.question_configs.archived_list_tooltip')}
        />
      </Box>
    )
  }

  const visibleQuestionConfigs = sortBy(
    questionCategory.questionConfigs,
    (schema) => schema.attributes.position
  )

  const archivedQuestionConfigs = sortBy(
    questionCategory.archivedQuestionConfigs,
    (schema) => schema.attributes.position
  )

  const onSortEnd = (items: QuestionConfig[]) => {
    items.forEach((item, index) => {
      reorderQuestionConfig({
        id: item.attributes.id,
        newPosition: index,
      })
    })
  }

  const collections: Array<{
    header?: JSX.Element
    items: QuestionConfig[]
    index: string
  }> = [
    {
      items: visibleQuestionConfigs,
      index: 'questions',
    },
  ]

  if (archivedQuestionConfigs.length) {
    collections.push({
      header: archivedQuestionsHeader(),
      items: archivedQuestionConfigs,
      index: 'archived-questions',
    })
  }

  return (
    <Box>
      <ExplotableList>
        <ExplotableItem
          scrollLimited={false}
          opened={open}
          renderCollectionHeader={() => questionsHeader(open)}
          toggle={toggle(!open)}
          fromCollection
        >
          {collections.map((collection, index) => (
            <React.Fragment key={`collection_${index}`}>
              {collection.header}
              <SortableBoxList
                key={`collection_sortable_${index}`}
                items={collection.items}
                onSortEnd={onSortEnd}
                emptyMessage={i18n.t('surveys.form.question_configs.no_question_configs')}
                itemAsKey={(item) => item.attributes.id}
                renderItem={(item) => (
                  <Item
                    form={form}
                    questionConfig={item}
                    updateQuestionConfig={updateQuestionConfig}
                    createQuestionConfigFromDuplicate={createQuestionConfigFromDuplicate}
                    toggleArchivedQuestionConfig={toggleArchivedQuestionConfig}
                    deleteQuestionConfig={deleteQuestionConfig}
                  />
                )}
              />
            </React.Fragment>
          ))}
        </ExplotableItem>
      </ExplotableList>
    </Box>
  )
}

export default observer(QuestionCategoryComponent)
